import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import BaseDialog from '../dialogs/BaseDialog';
import { GameListItem } from '../../services/contents_api';
import { TIER_KEYS, TIER_STYLES, isTierKey } from './tierConfig';

type AspectRatio = '16:9' | '1:1' | '9:16';

const ASPECT_DIMS: Record<AspectRatio, { w: number; h: number; label: string }> = {
  '16:9': { w: 1920, h: 1080, label: '16:9' },
  '1:1': { w: 1200, h: 1200, label: '1:1' },
  '9:16': { w: 1080, h: 1920, label: '9:16' },
};

const FONT_STACK =
  '"Inter", "SF Pro Display", "Helvetica Neue", "Hiragino Sans", "Hiragino Kaku Gothic ProN", "Noto Sans JP", system-ui, sans-serif';

// 画像キャッシュ
const imageCache = new Map<string, HTMLImageElement>();
const loadImage = (src: string): Promise<HTMLImageElement | null> => {
  if (!src) return Promise.resolve(null);
  if (imageCache.has(src)) return Promise.resolve(imageCache.get(src)!);
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      imageCache.set(src, img);
      resolve(img);
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });
};

// 角丸rect path
const roundedRectPath = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
) => {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.lineTo(x + w - rr, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + rr);
  ctx.lineTo(x + w, y + h - rr);
  ctx.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
  ctx.lineTo(x + rr, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - rr);
  ctx.lineTo(x, y + rr);
  ctx.quadraticCurveTo(x, y, x + rr, y);
  ctx.closePath();
};

// カバーを角丸付きで描画
const drawCover = (
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement | null,
  x: number,
  y: number,
  w: number,
  h: number,
  radius = 8
) => {
  ctx.save();
  roundedRectPath(ctx, x, y, w, h, radius);
  ctx.clip();
  if (img) {
    // object-fit: cover 相当
    const scale = Math.max(w / img.width, h / img.height);
    const drawW = img.width * scale;
    const drawH = img.height * scale;
    ctx.drawImage(img, x + (w - drawW) / 2, y + (h - drawH) / 2, drawW, drawH);
  } else {
    ctx.fillStyle = '#1f2028';
    ctx.fillRect(x, y, w, h);
  }
  ctx.restore();
};

const truncate = (
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number
): string => {
  if (ctx.measureText(text).width <= maxWidth) return text;
  let t = text;
  while (t.length > 0 && ctx.measureText(t + '…').width > maxWidth) {
    t = t.slice(0, -1);
  }
  return t + '…';
};

// 背景
const drawBackground = (ctx: CanvasRenderingContext2D, w: number, h: number) => {
  const g = ctx.createLinearGradient(0, 0, w, h);
  g.addColorStop(0, '#0E0F14');
  g.addColorStop(0.5, '#13151c');
  g.addColorStop(1, '#191b24');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, w, h);

  // 左上のソフトな発光
  const rg1 = ctx.createRadialGradient(0, 0, 0, 0, 0, w * 0.7);
  rg1.addColorStop(0, 'rgba(190, 255, 0, 0.12)');
  rg1.addColorStop(0.4, 'rgba(190, 255, 0, 0.03)');
  rg1.addColorStop(1, 'rgba(190, 255, 0, 0)');
  ctx.fillStyle = rg1;
  ctx.fillRect(0, 0, w, h);

  // 右下の補色発光
  const rg2 = ctx.createRadialGradient(w, h, 0, w, h, w * 0.7);
  rg2.addColorStop(0, 'rgba(0, 180, 255, 0.08)');
  rg2.addColorStop(1, 'rgba(0, 180, 255, 0)');
  ctx.fillStyle = rg2;
  ctx.fillRect(0, 0, w, h);
};

// ロゴ＋見出し領域
const drawHeader = async (
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  padding: number,
  data: {
    listTitle: string;
    userName: string;
    userAvatar?: string | null;
    listType: number;
    itemCount: number;
  }
): Promise<number> => {
  // 幾何平均ベースのスケール（アスペクト比に依らず安定）
  const scale = Math.sqrt(w * h) / 1200;

  // ロゴ
  const logo = await loadImage('/pulse_logo.png');
  const logoH = Math.round(32 * scale);
  let logoW = 0;
  if (logo) {
    logoW = (logo.width / logo.height) * logoH;
    ctx.globalAlpha = 0.85;
    ctx.drawImage(logo, w - padding - logoW, padding, logoW, logoH);
    ctx.globalAlpha = 1;
  }

  // タイプタグ
  const typeLabel =
    data.listType === 2 ? 'RANKING' : data.listType === 4 ? 'TIER LIST' : 'COLLECTION';
  const typeAccent =
    data.listType === 2
      ? '#ffd166'
      : data.listType === 4
      ? '#ef6171'
      : 'rgba(255,255,255,0.55)';
  ctx.font = `600 ${Math.round(14 * scale)}px ${FONT_STACK}`;
  ctx.fillStyle = typeAccent;
  ctx.textBaseline = 'top';
  ctx.fillText(typeLabel, padding, padding);

  // タイトル
  const titleY = padding + Math.round(24 * scale);
  ctx.font = `700 ${Math.round(36 * scale)}px ${FONT_STACK}`;
  ctx.fillStyle = 'rgba(255,255,255,0.97)';
  const titleMax = w - padding * 2 - (logo ? logoW + Math.round(20 * scale) : 0);
  ctx.fillText(truncate(ctx, data.listTitle, titleMax), padding, titleY);

  // サブテキスト (count + by user)
  ctx.font = `500 ${Math.round(18 * scale)}px ${FONT_STACK}`;
  ctx.fillStyle = 'rgba(255,255,255,0.55)';
  const subY = titleY + Math.round(46 * scale);
  const sub = `${data.itemCount} games  ·  by @${data.userName}`;
  ctx.fillText(sub, padding, subY);

  return subY + Math.round(28 * scale);
};

// フッターの透かし
const drawWatermark = (
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  padding: number
) => {
  const scale = Math.sqrt(w * h) / 1200;
  ctx.font = `500 ${Math.round(12 * scale)}px ${FONT_STACK}`;
  ctx.fillStyle = 'rgba(255,255,255,0.3)';
  ctx.textBaseline = 'alphabetic';
  ctx.textAlign = 'right';
  ctx.fillText('Generated on PULSE', w - padding, h - padding);
  ctx.textAlign = 'left';
};

// 順位チップ（カバー左上に置く、洗練ピル型）
const MEDAL_COLORS = ['#ffd166', '#c9c9d6', '#cf8e5e'];
const drawRankChip = (
  ctx: CanvasRenderingContext2D,
  rank: number,
  coverX: number,
  coverY: number,
  scale: number
) => {
  const isTop3 = rank <= 3;
  const bg = isTop3 ? MEDAL_COLORS[rank - 1] : 'rgba(0,0,0,0.78)';
  const fg = isTop3 ? 'rgba(0,0,0,0.88)' : 'rgba(255,255,255,0.95)';

  const rankStr = String(rank).padStart(2, '0');
  const fontSize = Math.max(10, Math.round((isTop3 ? 15 : 12) * scale));
  ctx.font = `700 ${fontSize}px ${FONT_STACK}`;
  const labelW = ctx.measureText(rankStr).width;
  const padX = Math.max(5, 7 * scale);
  const padY = Math.max(3, 4 * scale);
  const chipW = labelW + padX * 2;
  const chipH = fontSize + padY * 2;
  const chipX = coverX + Math.max(5, 7 * scale);
  const chipY = coverY + Math.max(5, 7 * scale);

  ctx.save();
  ctx.shadowColor = isTop3 ? `${bg}88` : 'rgba(0,0,0,0.5)';
  ctx.shadowBlur = isTop3 ? 10 * scale : 6 * scale;
  ctx.shadowOffsetY = 2 * scale;
  roundedRectPath(ctx, chipX, chipY, chipW, chipH, Math.max(3, chipH * 0.35));
  ctx.fillStyle = bg;
  ctx.fill();
  ctx.restore();

  ctx.fillStyle = fg;
  ctx.font = `700 ${fontSize}px ${FONT_STACK}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(rankStr, chipX + chipW / 2, chipY + chipH / 2);
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
};

// 最適な cols 選択: 縦の縮小も加味した「実セル幅」が最大になる cols を返す
//   - 単純な「幅 ÷ 固定値」では、縦が短くて cellW が縮む / 横に余白が残る という問題が出る
//   - 各 cols 候補で actualCellW (= cellW × 縦縮小率) を試算し、最大値を採用
//   - 小件数で巨大セルにならないよう maxCellW で minCols を底上げ
const chooseOptimalCols = (
  areaW: number,
  areaH: number,
  count: number
): number => {
  if (count <= 1) return 1;
  const gap = Math.max(8, Math.round(areaW * 0.012));
  const maxCellW = 300; // セル幅の上限（これより小さくなるよう最低カラム数を決める）
  const minCols = Math.max(
    1,
    Math.min(count, Math.ceil(areaW / (maxCellW + gap)))
  );

  let bestCols = minCols;
  let bestActualCellW = 0;

  for (let cols = minCols; cols <= count; cols++) {
    const cellW = (areaW - (cols - 1) * gap) / cols;
    if (cellW < 30) break; // 小さすぎたら打ち切り
    const cellH = cellW * (4 / 3);
    const rows = Math.ceil(count / cols);
    const totalH = rows * cellH + (rows - 1) * gap;
    const shrinkFactor = totalH > areaH ? areaH / totalH : 1;
    const actualCellW = cellW * shrinkFactor;
    if (actualCellW > bestActualCellW) {
      bestActualCellW = actualCellW;
      bestCols = cols;
    }
  }
  return bestCols;
};

// === Collection / 番号付きグリッド共通 ===
// startRank が指定された場合、各セルに順位チップを描く
const drawGrid = async (
  ctx: CanvasRenderingContext2D,
  items: GameListItem[],
  area: { x: number; y: number; w: number; h: number },
  opts?: { startRank?: number; fixedCellH?: number; medalTop3?: boolean }
) => {
  const total = items.length;
  if (total === 0) return;

  const cols = chooseOptimalCols(area.w, area.h, total);
  const rows = Math.ceil(total / cols);
  const gap = Math.max(8, Math.round(area.w * 0.012));
  const cellW = (area.w - gap * (cols - 1)) / cols;
  const cellH = opts?.fixedCellH ?? cellW * (4 / 3);

  let shrink = 1;
  if (!opts?.fixedCellH && cellH * rows + gap * (rows - 1) > area.h) {
    shrink = area.h / (cellH * rows + gap * (rows - 1));
  }
  const finalW = cellW * shrink;
  const finalH = cellH * shrink;
  const finalGap = gap * shrink;
  const gridW = finalW * cols + finalGap * (cols - 1);
  const gridH = finalH * rows + finalGap * (rows - 1);
  const offsetX = area.x + (area.w - gridW) / 2;
  const offsetY = area.y + (area.h - gridH) / 2;
  const cellScale = shrink;

  await Promise.all(
    items.map(async (item, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = offsetX + col * (finalW + finalGap);
      const y = offsetY + row * (finalH + finalGap);
      const img = await loadImage(item.game_cover || '');

      const rank = opts?.startRank !== undefined ? opts.startRank + i : null;

      ctx.save();
      // top-3 はメダル色の発光
      if (opts?.medalTop3 && rank !== null && rank <= 3) {
        ctx.shadowColor = `${MEDAL_COLORS[rank - 1]}66`;
        ctx.shadowBlur = 20 * cellScale;
      } else {
        ctx.shadowColor = 'rgba(0,0,0,0.5)';
        ctx.shadowBlur = 14 * cellScale;
      }
      ctx.shadowOffsetY = 5 * cellScale;
      drawCover(ctx, img, x, y, finalW, finalH, Math.max(4, 8 * cellScale));
      ctx.restore();

      // top-3 は上端にメタリックなヘアライン
      if (opts?.medalTop3 && rank !== null && rank <= 3) {
        const color = MEDAL_COLORS[rank - 1];
        const lineGrad = ctx.createLinearGradient(x, y, x + finalW, y);
        lineGrad.addColorStop(0, 'transparent');
        lineGrad.addColorStop(0.5, color);
        lineGrad.addColorStop(1, 'transparent');
        ctx.save();
        ctx.fillStyle = lineGrad;
        ctx.fillRect(x, y, finalW, Math.max(2, 3 * cellScale));
        ctx.restore();
      }

      if (rank !== null) {
        drawRankChip(ctx, rank, x, y, cellScale);
      }
    })
  );
};

const drawCollection = async (
  ctx: CanvasRenderingContext2D,
  items: GameListItem[],
  area: { x: number; y: number; w: number; h: number }
) => {
  await drawGrid(ctx, items, area);
};

// === Ranking ===
// TOP3 を大きめの均等な行 + メタリックアクセント
// それ以外を番号付きグリッドで
const drawRanking = async (
  ctx: CanvasRenderingContext2D,
  items: GameListItem[],
  area: { x: number; y: number; w: number; h: number }
) => {
  if (items.length === 0) return;
  const scale = Math.sqrt(area.w * area.h) / 1000;

  const top3 = items.slice(0, 3);
  const rest = items.slice(3);

  // TOP3 行: 均等3カラム、ステアケースなし
  const top3Gap = Math.max(16, Math.round(24 * scale));
  let top3CoverW = (area.w - top3Gap * 2) / 3;
  let top3CoverH = top3CoverW * (4 / 3);
  // 高さの最大を area.h * 0.45 に制限
  const maxTop3H = area.h * (rest.length > 0 ? 0.42 : 0.7);
  if (top3CoverH > maxTop3H) {
    top3CoverH = maxTop3H;
    top3CoverW = top3CoverH * (3 / 4);
  }
  const top3RowW = top3CoverW * 3 + top3Gap * 2;
  const top3StartX = area.x + (area.w - top3RowW) / 2;
  const top3Y = area.y;

  await Promise.all(
    top3.map(async (item, i) => {
      const x = top3StartX + i * (top3CoverW + top3Gap);
      const y = top3Y;
      const img = await loadImage(item.game_cover || '');
      const color = MEDAL_COLORS[i];

      ctx.save();
      ctx.shadowColor = `${color}55`;
      ctx.shadowBlur = 26 * scale;
      drawCover(ctx, img, x, y, top3CoverW, top3CoverH, Math.max(8, 12 * scale));
      ctx.restore();

      // 上端メタリックヘアライン
      const lineGrad = ctx.createLinearGradient(x, y, x + top3CoverW, y);
      lineGrad.addColorStop(0, 'transparent');
      lineGrad.addColorStop(0.5, color);
      lineGrad.addColorStop(1, 'transparent');
      ctx.save();
      ctx.fillStyle = lineGrad;
      ctx.fillRect(x, y, top3CoverW, Math.max(2, 4 * scale));
      ctx.restore();

      drawRankChip(ctx, i + 1, x, y, scale);
    })
  );

  if (rest.length === 0) return;

  const restY = top3Y + top3CoverH + Math.round(32 * scale);
  const restH = area.h - (top3CoverH + Math.round(32 * scale));
  await drawGrid(
    ctx,
    rest,
    { x: area.x, y: restY, w: area.w, h: restH },
    { startRank: 4 }
  );
};

// === Tier List ===
// 個別の Tier 行を描画
const renderTierRow = async (
  ctx: CanvasRenderingContext2D,
  rowArea: { x: number; y: number; w: number; h: number },
  label: string,
  accent: string,
  rowItems: GameListItem[],
  scale: number
) => {
  const radius = Math.round(12 * scale);
  // 行背景: ページと同じ控えめな左→右フェード
  const rowBgGrad = ctx.createLinearGradient(
    rowArea.x,
    rowArea.y,
    rowArea.x + rowArea.w,
    rowArea.y
  );
  rowBgGrad.addColorStop(0, `${accent}12`);
  rowBgGrad.addColorStop(0.3, `${accent}06`);
  rowBgGrad.addColorStop(0.7, 'rgba(0,0,0,0)');
  rowBgGrad.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.save();
  roundedRectPath(ctx, rowArea.x, rowArea.y, rowArea.w, rowArea.h, radius);
  ctx.fillStyle = rowBgGrad;
  ctx.fill();
  ctx.strokeStyle = 'rgba(255,255,255,0.06)';
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.restore();

  const labelColW = Math.max(56 * scale, rowArea.h * 0.85);
  const itemAreaPadding = Math.round(12 * scale);

  // ラベルチップ: ページのガラスチップ風に揃える
  const chipSize = Math.min(rowArea.h * 0.55, labelColW * 0.75);
  const chipX = rowArea.x + (labelColW - chipSize) / 2;
  const chipY = rowArea.y + (rowArea.h - chipSize) / 2;
  const chipRadius = Math.round(8 * scale);
  ctx.save();
  // 控えめな外側シャドウ
  ctx.shadowColor = `${accent}22`;
  ctx.shadowBlur = 6 * scale;
  const chipGrad = ctx.createLinearGradient(
    chipX,
    chipY,
    chipX + chipSize,
    chipY + chipSize
  );
  chipGrad.addColorStop(0, `${accent}38`);
  chipGrad.addColorStop(1, `${accent}18`);
  roundedRectPath(ctx, chipX, chipY, chipSize, chipSize, chipRadius);
  ctx.fillStyle = chipGrad;
  ctx.fill();
  ctx.restore();
  // ボーダー
  ctx.save();
  roundedRectPath(ctx, chipX, chipY, chipSize, chipSize, chipRadius);
  ctx.strokeStyle = `${accent}5e`;
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.restore();
  // 内側上端のハイライト (ページの inset shadow を擬似的に再現)
  ctx.save();
  ctx.fillStyle = `${accent}33`;
  ctx.fillRect(
    chipX + chipRadius,
    chipY + 1,
    chipSize - chipRadius * 2,
    1
  );
  ctx.restore();

  // ラベル文字 (ページと同じ weight 700 + テキスト発光)
  ctx.save();
  ctx.shadowColor = `${accent}77`;
  ctx.shadowBlur = 10 * scale;
  ctx.fillStyle = accent;
  ctx.font = `700 ${Math.round(chipSize * 0.55)}px ${FONT_STACK}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, chipX + chipSize / 2, chipY + chipSize / 2);
  ctx.textAlign = 'left';
  ctx.restore();

  // カバー描画エリア
  const itemAreaX = rowArea.x + labelColW + itemAreaPadding;
  const itemAreaY = rowArea.y + itemAreaPadding;
  const itemAreaW = rowArea.w - labelColW - itemAreaPadding * 2;
  const itemAreaH = rowArea.h - itemAreaPadding * 2;

  if (rowItems.length === 0) return;

  // カバー基本サイズ: 行高基準
  let coverH = itemAreaH;
  let coverW = coverH * (3 / 4);
  const gap = Math.round(8 * scale);
  const maxFit = Math.floor((itemAreaW + gap) / (coverW + gap));

  if (rowItems.length > maxFit) {
    coverW = (itemAreaW - gap * (rowItems.length - 1)) / rowItems.length;
    coverW = Math.max(coverW, 32 * scale);
    coverH = coverW * (4 / 3);
    if (coverH > itemAreaH) {
      coverH = itemAreaH;
      coverW = coverH * (3 / 4);
    }
  }

  const totalW = coverW * rowItems.length + gap * (rowItems.length - 1);
  const showOverflow = totalW > itemAreaW;
  const fitCount = showOverflow
    ? Math.max(1, Math.floor((itemAreaW + gap) / (coverW + gap)) - 1)
    : rowItems.length;

  await Promise.all(
    rowItems.slice(0, fitCount).map(async (item, i) => {
      const img = await loadImage(item.game_cover || '');
      const x = itemAreaX + i * (coverW + gap);
      const yPos = itemAreaY + (itemAreaH - coverH) / 2;
      ctx.save();
      ctx.shadowColor = 'rgba(0,0,0,0.45)';
      ctx.shadowBlur = 10 * scale;
      ctx.shadowOffsetY = 3 * scale;
      drawCover(ctx, img, x, yPos, coverW, coverH, Math.max(4, 6 * scale));
      ctx.restore();
    })
  );

  // オーバーフロー表示
  if (showOverflow && fitCount < rowItems.length) {
    const remaining = rowItems.length - fitCount;
    const x = itemAreaX + fitCount * (coverW + gap);
    const yPos = itemAreaY + (itemAreaH - coverH) / 2;
    ctx.save();
    roundedRectPath(ctx, x, yPos, coverW, coverH, Math.max(4, 6 * scale));
    ctx.fillStyle = 'rgba(255,255,255,0.06)';
    ctx.fill();
    ctx.strokeStyle = 'rgba(255,255,255,0.12)';
    ctx.stroke();
    ctx.fillStyle = 'rgba(255,255,255,0.7)';
    ctx.font = `700 ${Math.round(coverW * 0.28)}px ${FONT_STACK}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(`+${remaining}`, x + coverW / 2, yPos + coverH / 2);
    ctx.textAlign = 'left';
    ctx.restore();
  }
};

const drawTierList = async (
  ctx: CanvasRenderingContext2D,
  items: GameListItem[],
  area: { x: number; y: number; w: number; h: number }
) => {
  const scale = Math.sqrt(area.w * area.h) / 1000;
  const unranked = items.filter((it) => !isTierKey(it.tier));
  const showUnranked = unranked.length > 0;
  const rowGap = Math.round(10 * scale);
  // 横長(16:9)では2列レイアウトで1行を厚く取る
  const useTwoColumns = area.w / area.h > 1.3;

  if (useTwoColumns) {
    const colGap = Math.round(14 * scale);
    const colW = (area.w - colGap) / 2;
    const unrankedH = showUnranked
      ? Math.max(area.h * 0.22, 100 * scale)
      : 0;
    const unrankedGap = showUnranked ? rowGap : 0;
    const tierAreaH = area.h - unrankedH - unrankedGap;
    const rowsPerCol = 3;
    const rowH = (tierAreaH - rowGap * (rowsPerCol - 1)) / rowsPerCol;

    const leftTiers = TIER_KEYS.slice(0, 3);   // S, A, B
    const rightTiers = TIER_KEYS.slice(3);     // C, D, F

    for (let i = 0; i < rowsPerCol; i++) {
      const y = area.y + i * (rowH + rowGap);
      const leftTier = leftTiers[i];
      const rightTier = rightTiers[i];
      await renderTierRow(
        ctx,
        { x: area.x, y, w: colW, h: rowH },
        leftTier,
        TIER_STYLES[leftTier].accent,
        items.filter((it) => it.tier === leftTier),
        scale
      );
      await renderTierRow(
        ctx,
        { x: area.x + colW + colGap, y, w: colW, h: rowH },
        rightTier,
        TIER_STYLES[rightTier].accent,
        items.filter((it) => it.tier === rightTier),
        scale
      );
    }

    if (showUnranked) {
      await renderTierRow(
        ctx,
        {
          x: area.x,
          y: area.y + tierAreaH + unrankedGap,
          w: area.w,
          h: unrankedH,
        },
        '?',
        'rgba(255,255,255,0.4)',
        unranked,
        scale
      );
    }
    return;
  }

  // 単列レイアウト（1:1, 9:16）
  const rowCount = TIER_KEYS.length + (showUnranked ? 1 : 0);
  const rowH = (area.h - rowGap * (rowCount - 1)) / rowCount;

  for (let i = 0; i < TIER_KEYS.length; i++) {
    const tier = TIER_KEYS[i];
    const y = area.y + i * (rowH + rowGap);
    await renderTierRow(
      ctx,
      { x: area.x, y, w: area.w, h: rowH },
      tier,
      TIER_STYLES[tier].accent,
      items.filter((it) => it.tier === tier),
      scale
    );
  }

  if (showUnranked) {
    const y = area.y + TIER_KEYS.length * (rowH + rowGap);
    await renderTierRow(
      ctx,
      { x: area.x, y, w: area.w, h: rowH },
      '?',
      'rgba(255,255,255,0.4)',
      unranked,
      scale
    );
  }
};

// ===== ゆったり (Comfortable) モード =====
// 必要キャンバス高を事前計算
const computeTierListBodyH = (
  items: GameListItem[],
  innerW: number,
  scale: number
): number => {
  const targetCoverH = Math.round(200 * scale);
  const coverW = Math.round(targetCoverH * 0.75);
  const tileGap = Math.round(8 * scale);
  const labelColW = Math.max(56 * scale, targetCoverH * 0.85);
  const itemAreaPad = Math.round(12 * scale);
  const rowGap = Math.round(10 * scale);

  const tileAreaW = innerW - labelColW - itemAreaPad * 2;
  const itemsPerRow = Math.max(
    1,
    Math.floor((tileAreaW + tileGap) / (coverW + tileGap))
  );

  let totalH = 0;
  for (let i = 0; i < TIER_KEYS.length; i++) {
    const tier = TIER_KEYS[i];
    const tierItems = items.filter((it) => it.tier === tier);
    const numRows = Math.max(1, Math.ceil(tierItems.length / itemsPerRow));
    const tierH =
      numRows * targetCoverH + (numRows - 1) * tileGap + itemAreaPad * 2;
    totalH += tierH;
    if (i < TIER_KEYS.length - 1) totalH += rowGap;
  }

  const unranked = items.filter((it) => !isTierKey(it.tier));
  if (unranked.length > 0) {
    const numRows = Math.max(1, Math.ceil(unranked.length / itemsPerRow));
    const unrankedH =
      numRows * targetCoverH + (numRows - 1) * tileGap + itemAreaPad * 2;
    totalH += rowGap + unrankedH;
  }

  return totalH;
};

const computeComfortableHeight = (
  items: GameListItem[],
  baseW: number
): number => {
  const scale = baseW / 1200;
  const padding = Math.round(36 * scale);
  const innerW = baseW - padding * 2;

  const bodyH = computeTierListBodyH(items, innerW, scale);

  // header content (98*scale) + top padding + body top gap (12) + body + footer space (40) + bottom padding
  return Math.round(2 * padding + 98 * scale + 12 * scale + bodyH + 40 * scale);
};

// === ゆったり Tier List ===
// 各 tier 行が必要分だけ縦に伸びる
const drawTierListComfortable = async (
  ctx: CanvasRenderingContext2D,
  items: GameListItem[],
  area: { x: number; y: number; w: number; h: number },
  scale: number
) => {
  const unranked = items.filter((it) => !isTierKey(it.tier));
  const showUnranked = unranked.length > 0;
  const targetCoverH = Math.round(200 * scale);
  const coverW = Math.round(targetCoverH * 0.75);
  const tileGap = Math.round(8 * scale);
  const labelColW = Math.max(56 * scale, targetCoverH * 0.85);
  const itemAreaPad = Math.round(12 * scale);
  const rowGap = Math.round(10 * scale);

  const tileAreaW = area.w - labelColW - itemAreaPad * 2;
  const itemsPerRow = Math.max(
    1,
    Math.floor((tileAreaW + tileGap) / (coverW + tileGap))
  );

  let curY = area.y;

  const renderRow = async (
    label: string,
    accent: string,
    rowItems: GameListItem[]
  ) => {
    const numRows = Math.max(1, Math.ceil(rowItems.length / itemsPerRow));
    const tierH =
      numRows * targetCoverH + (numRows - 1) * tileGap + itemAreaPad * 2;

    const radius = Math.round(12 * scale);
    // 行背景: ページと同じ控えめなフェード
    const rowBgGrad = ctx.createLinearGradient(
      area.x,
      curY,
      area.x + area.w,
      curY
    );
    rowBgGrad.addColorStop(0, `${accent}12`);
    rowBgGrad.addColorStop(0.3, `${accent}06`);
    rowBgGrad.addColorStop(0.7, 'rgba(0,0,0,0)');
    rowBgGrad.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.save();
    roundedRectPath(ctx, area.x, curY, area.w, tierH, radius);
    ctx.fillStyle = rowBgGrad;
    ctx.fill();
    ctx.strokeStyle = 'rgba(255,255,255,0.06)';
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.restore();

    // ラベルチップ (ページのガラスチップ風)
    const chipSize = Math.min(targetCoverH * 0.55, labelColW * 0.75);
    const chipX = area.x + (labelColW - chipSize) / 2;
    const chipY = curY + (tierH - chipSize) / 2;
    const chipRadius = Math.round(8 * scale);
    ctx.save();
    ctx.shadowColor = `${accent}22`;
    ctx.shadowBlur = 6 * scale;
    const chipGrad = ctx.createLinearGradient(
      chipX,
      chipY,
      chipX + chipSize,
      chipY + chipSize
    );
    chipGrad.addColorStop(0, `${accent}38`);
    chipGrad.addColorStop(1, `${accent}18`);
    roundedRectPath(ctx, chipX, chipY, chipSize, chipSize, chipRadius);
    ctx.fillStyle = chipGrad;
    ctx.fill();
    ctx.restore();
    ctx.save();
    roundedRectPath(ctx, chipX, chipY, chipSize, chipSize, chipRadius);
    ctx.strokeStyle = `${accent}5e`;
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.restore();
    // 内側上端ハイライト
    ctx.save();
    ctx.fillStyle = `${accent}33`;
    ctx.fillRect(
      chipX + chipRadius,
      chipY + 1,
      chipSize - chipRadius * 2,
      1
    );
    ctx.restore();

    // ラベル文字 (weight 700 + 発光)
    ctx.save();
    ctx.shadowColor = `${accent}77`;
    ctx.shadowBlur = 10 * scale;
    ctx.fillStyle = accent;
    ctx.font = `700 ${Math.round(chipSize * 0.55)}px ${FONT_STACK}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, chipX + chipSize / 2, chipY + chipSize / 2);
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    ctx.restore();

    // カバー (multi-row wrapping)
    const itemAreaX = area.x + labelColW + itemAreaPad;
    const itemAreaY = curY + itemAreaPad;

    await Promise.all(
      rowItems.map(async (item, i) => {
        const col = i % itemsPerRow;
        const row = Math.floor(i / itemsPerRow);
        const x = itemAreaX + col * (coverW + tileGap);
        const y = itemAreaY + row * (targetCoverH + tileGap);
        const img = await loadImage(item.game_cover || '');
        ctx.save();
        ctx.shadowColor = 'rgba(0,0,0,0.45)';
        ctx.shadowBlur = 10 * scale;
        ctx.shadowOffsetY = 3 * scale;
        drawCover(ctx, img, x, y, coverW, targetCoverH, Math.max(4, 6 * scale));
        ctx.restore();
      })
    );

    curY += tierH + rowGap;
  };

  for (const tier of TIER_KEYS) {
    await renderRow(
      tier,
      TIER_STYLES[tier].accent,
      items.filter((it) => it.tier === tier)
    );
  }

  if (showUnranked) {
    await renderRow('?', 'rgba(255,255,255,0.4)', unranked);
  }
};

interface ShareImageDialogProps {
  open: boolean;
  onClose: () => void;
  items: GameListItem[];
  listTitle: string;
  userName: string;
  userAvatar?: string | null;
  listType: number;
}

type Density = 'compact' | 'comfortable';

const ShareImageDialog: React.FC<ShareImageDialogProps> = ({
  open,
  onClose,
  items,
  listTitle,
  userName,
  userAvatar,
  listType,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [aspect, setAspect] = useState<AspectRatio>('1:1');
  const [density, setDensity] = useState<Density>('compact');
  const [drawing, setDrawing] = useState(false);

  // Tier モードで、いずれかの Tier が折り返し閾値を超える件数を持つかどうか。
  // 全 Tier が小件数なら折り返しは発生しないので、トグル自体を隠す。
  const hasWrappingTier = useMemo(() => {
    if (listType !== 4) return false;
    const counts = TIER_KEYS.map(
      (t) => items.filter((it) => it.tier === t).length
    );
    const unrankedCount = items.filter((it) => !isTierKey(it.tier)).length;
    // 1:1 / 9:16 のどちらでも 1 行に最大 5 件まで → 6 件以上で折り返し
    return Math.max(...counts, unrankedCount) > 5;
  }, [listType, items]);

  const renderImage = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    setDrawing(true);

    if (document.fonts && document.fonts.ready) {
      try {
        await document.fonts.ready;
      } catch {
        // ignore
      }
    }

    // comfortable は「Tier かつ折り返しが発生する」場合のみ意味がある
    const effectiveDensity =
      listType === 4 && hasWrappingTier ? density : 'compact';

    const { w: baseW, h: baseH } = ASPECT_DIMS[aspect];
    const w = baseW;
    let h = baseH;

    if (effectiveDensity === 'comfortable') {
      // 折り返しモードはコンテンツに合わせて伸縮する設計
      // baseH との max を取ると下が余白だらけになるので使わない
      h = computeComfortableHeight(items, baseW);
    }

    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      setDrawing(false);
      return;
    }

    const baseScale =
      effectiveDensity === 'comfortable'
        ? w / 1200
        : Math.sqrt(w * h) / 1200;
    const padding = Math.round(36 * baseScale);

    drawBackground(ctx, w, h);

    const headerBottom = await drawHeader(ctx, w, h, padding, {
      listTitle,
      userName,
      userAvatar,
      listType,
      itemCount: items.length,
    });

    const footerSpace = Math.round(40 * baseScale);
    const bodyArea = {
      x: padding,
      y: headerBottom + Math.round(12 * baseScale),
      w: w - padding * 2,
      h: h - headerBottom - footerSpace - padding,
    };

    if (effectiveDensity === 'comfortable' && listType === 4) {
      await drawTierListComfortable(ctx, items, bodyArea, baseScale);
    } else if (listType === 4) {
      await drawTierList(ctx, items, bodyArea);
    } else if (listType === 2) {
      await drawRanking(ctx, items, bodyArea);
    } else {
      await drawCollection(ctx, items, bodyArea);
    }

    drawWatermark(ctx, w, h, padding);

    setDrawing(false);
  }, [aspect, density, hasWrappingTier, items, listTitle, userName, userAvatar, listType]);

  // Dialog mount 後の確実な初回描画のため rAF 経由で起動
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const id = requestAnimationFrame(() => {
      if (!cancelled) renderImage();
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(id);
    };
  }, [open, renderImage]);

  const handleDownload = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const fileName = `${listTitle || 'list'}-${aspect.replace(':', 'x')}.png`;
    if (/Android|webOS|iPhone|iPad|iPod/i.test(navigator.userAgent)) {
      const dataUrl = canvas.toDataURL('image/png');
      const byteString = atob(dataUrl.split(',')[1]);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
      const blob = new Blob([ab], { type: 'image/png' });
      const url = URL.createObjectURL(blob);
      window.open(url);
      setTimeout(() => URL.revokeObjectURL(url), 500);
    } else {
      const link = document.createElement('a');
      link.download = fileName;
      link.href = canvas.toDataURL('image/png');
      link.click();
    }
  };

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title="リスト画像を作成"
      maxWidth="md"
      actions={
        <>
          <Button onClick={onClose} sx={{ color: 'text.secondary' }}>
            閉じる
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            disabled={drawing}
          >
            ダウンロード
          </Button>
        </>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
            rowGap: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontSize: '0.62rem',
                fontWeight: 600,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
              }}
            >
              Aspect
            </Typography>
            <ToggleButtonGroup
              value={aspect}
              exclusive
              onChange={(_, v) => v && setAspect(v as AspectRatio)}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  px: 1.5,
                  py: 0.5,
                  fontSize: '0.7rem',
                  textTransform: 'none',
                  letterSpacing: '0.02em',
                  color: 'text.secondary',
                  borderColor: 'rgba(255,255,255,0.1)',
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(190, 255, 0, 0.12)',
                    color: 'rgba(255,255,255,0.95)',
                    borderColor: 'rgba(190, 255, 0, 0.4)',
                  },
                },
              }}
            >
              {(Object.keys(ASPECT_DIMS) as AspectRatio[])
                // Tier は階層スタック構造のため横長 (16:9) と相性が悪い → 非表示
                .filter((k) => !(listType === 4 && k === '16:9'))
                .map((k) => (
                  <ToggleButton key={k} value={k}>
                    {ASPECT_DIMS[k].label}
                  </ToggleButton>
                ))}
            </ToggleButtonGroup>
          </Box>
          {/* density は Tier リストでのみ意味がある（Tier 間のサイズ混在を防ぐため）
              Collection/Ranking は元々サイズ均等なので非表示。
              さらに、折り返しが発生しない件数なら Tier でもトグル自体を出さない */}
          {listType === 4 && hasWrappingTier && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.62rem',
                  fontWeight: 600,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                }}
              >
                Layout
              </Typography>
              <ToggleButtonGroup
                value={density}
                exclusive
                onChange={(_, v) => v && setDensity(v as Density)}
                size="small"
                sx={{
                  '& .MuiToggleButton-root': {
                    px: 1.5,
                    py: 0.5,
                    fontSize: '0.7rem',
                    textTransform: 'none',
                    letterSpacing: '0.02em',
                    color: 'text.secondary',
                    borderColor: 'rgba(255,255,255,0.1)',
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(190, 255, 0, 0.12)',
                      color: 'rgba(255,255,255,0.95)',
                      borderColor: 'rgba(190, 255, 0, 0.4)',
                    },
                  },
                }}
              >
                <ToggleButton value="compact">詰める</ToggleButton>
                <ToggleButton value="comfortable">折り返す</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}
        </Box>
        {listType === 4 && hasWrappingTier && density === 'comfortable' && (
          <Typography
            variant="caption"
            sx={{ color: 'text.disabled', fontSize: '0.65rem', mt: -0.5 }}
          >
            折り返しモードでは Tier 間でカバーサイズが揃い、画像が縦に伸びます
          </Typography>
        )}

        <Box
          sx={{
            position: 'relative',
            borderRadius: 1.5,
            overflow: 'hidden',
            border: '1px solid rgba(255,255,255,0.08)',
            backgroundColor: 'rgba(0,0,0,0.35)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 240,
            maxHeight: { xs: '52vh', md: '58vh' },
          }}
        >
          <canvas
            ref={canvasRef}
            style={{
              display: 'block',
              maxWidth: '100%',
              maxHeight: '58vh',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
            }}
          />
          {drawing && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(0,0,0,0.4)',
              }}
            >
              <CircularProgress size={32} sx={{ color: 'rgba(190,255,0,0.8)' }} />
            </Box>
          )}
        </Box>

        <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem' }}>
          画像はリストURLと併せてお使いください。
        </Typography>
      </Box>
    </BaseDialog>
  );
};

export default ShareImageDialog;
